import * as H from 'history';
import { NavigateFunction } from 'react-router-dom-v5-compat';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import SessionClientOnboard from 'dashboard/lib/clients/SessionClientOnboard';
import { Role } from 'dashboard/models/role';
import { MfaMethod, User } from 'dashboard/models/user';
import { UserAuthentication, UserAuthenticationStatus } from 'dashboard/models/user-authentication';
import { CurrentUserSessionMeta, UserSession } from 'dashboard/models/user-session';
import {
  clearAuthenticationState,
  setUserAuthentication,
  startMfaMethod,
} from 'dashboard/redux/actions/user-authentication';
import { fetchDashboardData } from 'dashboard/redux/thunk-actions/dashboard';
import { DashboardThunkAction } from 'dashboard/redux/types';
import { ApiModel, ApiResponse } from 'lib/interfaces/api';
import { captureException } from 'lib/reporting';
import { assertNever } from 'lib/utils/types';

const redirectOnLogin =
  (location: H.Location<{ from?: string }>, navigate: NavigateFunction): DashboardThunkAction =>
  (): void => {
    const from = location.state?.from;
    const defaultLocation = '/dashboard';

    const destination = typeof from === 'string' ? from : defaultLocation;

    navigate(destination);
  };

interface CreateUserAuthenticationArgs {
  emailAddress: string;
  password: string;
  location: H.Location<H.LocationState>;
  navigate: NavigateFunction;
}

export const createUserAuthentication =
  ({
    emailAddress,
    password,
    location,
    navigate,
  }: CreateUserAuthenticationArgs): DashboardThunkAction =>
  async (dispatch, ...args): Promise<void> => {
    const { data, included, meta } = await SessionClientOnboard.postResourceCreate<
      UserAuthentication,
      { type: ApiModel.UserAuthentication },
      CurrentUserSessionMeta
    >(
      { type: ApiModel.UserAuthentication },
      {
        meta: {
          'email-address': emailAddress,
          password,
        },
      },
    );

    const {
      id,
      attributes: { 'available-mfa-methods': availableMfaMethods, status },
    } = data;

    switch (status) {
      case UserAuthenticationStatus.Success: {
        // If included or meta are undefined, something went terribly wrong, log it and try to find the symptoms
        // Trying to debug https://persona-identities-inc.sentry.io/issues/4347468127/
        if (included == null || meta == null) {
          const missingValues = { included: !!included, meta: !!meta };
          captureException(
            new Error(`UserAuthentication model is missing data: ${JSON.stringify(missingValues)}`),
          );
          break;
        }

        const user = included.find(({ id }) => id === data.relationships.user.data.id) as User;
        const roleIds = user.relationships.roles.data.map((roleData) => roleData.id);

        // Route Dashboard API requests to servers based off country from backend for current session
        SessionClientDashboard.personaCountry = SessionClientOnboard.personaCountry;

        await fetchDashboardData({
          'access-token': meta['access-token'],
          'refresh-token': meta['refresh-token'],
          'feature-flags': meta['feature-flags'],
          'impersonation-session': meta['impersonation-session'],
          'readme-jwt-token': meta['readme-jwt-token'],
          'expires-at': meta['expires-at'],
          user,
          userSession: included.find(
            ({ id }) => id === data.relationships['user-session'].data.id,
          ) as UserSession,
          roles: included.filter(({ id }) => roleIds.includes(id)) as Role[],
        })(dispatch, ...args);

        dispatch(redirectOnLogin(location, navigate));

        break;
      }
      case UserAuthenticationStatus.Pending:
        dispatch(setUserAuthentication(data));

        // Preload MFA selection state if there is only 1 selection
        if (availableMfaMethods?.length === 1 && availableMfaMethods?.[0]) {
          await startMfa(id, availableMfaMethods[0])(dispatch, ...args);
        }
        navigate('/dashboard/login-2fa', { state: location.state });
        break;
      case UserAuthenticationStatus.Failed:
      case UserAuthenticationStatus.Expired:
        break;
      default:
        assertNever(status);
    }
  };

export const startMfa =
  (id: string, type: MfaMethod): DashboardThunkAction =>
  async (dispatch): Promise<void> => {
    switch (type) {
      case MfaMethod.Totp:
      case MfaMethod.RecoveryCode:
        // Do nothing -- can send code without starting process on UserSession.
        dispatch(startMfaMethod(type));
        break;
      case MfaMethod.Email:
        await SessionClientOnboard.postResourceAction<UserAuthentication>(
          { id, type: ApiModel.UserAuthentication },
          'start-mfa-email',
        );
        dispatch(startMfaMethod(type));
        break;
      case MfaMethod.Phone:
        await SessionClientOnboard.postResourceAction<UserAuthentication>(
          { id, type: ApiModel.UserAuthentication },
          'start-mfa-phone',
        );
        dispatch(startMfaMethod(type));
        break;
      default:
        assertNever(type);
    }
  };

interface ActivateMfaArgs {
  id: string;
  method: MfaMethod;
  code: string;
  location: H.Location<H.LocationState>;
}

export const activateMfa =
  ({ id, method, code }: ActivateMfaArgs): DashboardThunkAction =>
  async (dispatch, ...args): Promise<void> => {
    let response: ApiResponse<UserAuthentication, CurrentUserSessionMeta>;
    switch (method) {
      case MfaMethod.Totp: {
        response = await SessionClientOnboard.postResourceAction<
          UserAuthentication,
          any,
          CurrentUserSessionMeta
        >(
          {
            id,
            type: ApiModel.UserAuthentication,
          },
          'activate-mfa-totp',
          {
            meta: {
              'mfa-totp-code': code,
            },
          },
        );
        break;
      }
      case MfaMethod.Email:
        response = await SessionClientOnboard.postResourceAction<
          UserAuthentication,
          any,
          CurrentUserSessionMeta
        >(
          {
            id,
            type: ApiModel.UserAuthentication,
          },
          'activate-mfa-email',
          {
            meta: {
              'mfa-email-code': code,
            },
          },
        );
        break;
      case MfaMethod.Phone:
        response = await SessionClientOnboard.postResourceAction<
          UserAuthentication,
          any,
          CurrentUserSessionMeta
        >(
          {
            id,
            type: ApiModel.UserAuthentication,
          },
          'activate-mfa-phone',
          {
            meta: {
              'mfa-phone-code': code,
            },
          },
        );
        break;
      case MfaMethod.RecoveryCode:
        response = await SessionClientOnboard.postResourceAction<
          UserAuthentication,
          any,
          CurrentUserSessionMeta
        >(
          {
            id,
            type: ApiModel.UserAuthentication,
          },
          'activate-recovery-code',
          {
            meta: {
              'recovery-code': code,
            },
          },
        );
        break;
      default:
        assertNever(method);
    }

    const { data, included, meta } = response;

    const user = included?.find(({ id }) => id === data.relationships.user.data.id) as User;
    const roleIds = user?.relationships.roles.data.map((roleData) => roleData.id) || [];

    // If included or meta are undefined, something went terribly wrong, log it and try to find the symptoms
    // Trying to debug https://persona-identities-inc.sentry.io/issues/4347468127/
    if (included == null || meta == null) {
      const missingValues = { included: !!included, meta: !!meta };
      captureException(
        new Error(`UserAuthentication model is missing data: ${JSON.stringify(missingValues)}`),
      );
      return;
    }

    // Route Dashboard API requests to servers based off country from backend for current session
    SessionClientDashboard.personaCountry = SessionClientOnboard.personaCountry;
    await fetchDashboardData({
      'access-token': meta['access-token'],
      'refresh-token': meta['refresh-token'],
      'feature-flags': meta['feature-flags'],
      'impersonation-session': meta['impersonation-session'],
      'readme-jwt-token': meta['readme-jwt-token'],
      'expires-at': meta['expires-at'],
      user: user,
      userSession: included?.find(
        ({ id }) => id === data.relationships['user-session'].data.id,
      ) as UserSession,
      roles: (included ?? []).filter((role): role is Role => roleIds.includes(role.id)),
    })(dispatch, ...args);

    dispatch(clearAuthenticationState());
  };
